import React, { useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";

async function api(url, opt) {
  const r = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opt,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw Error(j.error || "Request failed");
  return j;
}

const isIos = () => /iphone|ipad|ipod/.test(window.navigator.userAgent.toLowerCase());
const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.navigator.standalone === true;

export default function FamilyMobilePortal() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ request_type: "Additional Coverage" });
  const [msg, setMsg] = useState("");
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showIosHelp, setShowIosHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());
  const [photos, setPhotos] = useState([]);

  const load = () =>
    api("/api/enterprise/family/dashboard").then(setD).catch((e) => setErr(e.message));
  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (d?.client_id) {
      api(`/api/clients/${d.client_id}/photos`)
        .then((r) => setPhotos(r.photos || []))
        .catch(() => {});
    }
  }, [d?.client_id]);

  // --- Install as an app (PWA) --------------------------------------------
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return; // already inside the native app shell

    const manifestLink = document.createElement("link");
    manifestLink.rel = "manifest";
    manifestLink.href = "/manifest-family.json";
    document.head.appendChild(manifestLink);

    const themeMeta = document.createElement("meta");
    themeMeta.name = "theme-color";
    themeMeta.content = "#1B3A6B";
    document.head.appendChild(themeMeta);

    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw-family.js", { scope: "/family" }).catch(() => {});
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setInstallPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      document.head.removeChild(manifestLink);
      document.head.removeChild(themeMeta);
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (installPrompt) {
      installPrompt.prompt();
      await installPrompt.userChoice;
      setInstallPrompt(null);
    } else if (isIos()) {
      setShowIosHelp(true);
    }
  };
  // -------------------------------------------------------------------------

  const request = async () => {
    await api("/api/enterprise/family/requests", {
      method: "POST",
      body: JSON.stringify({ ...form, client_id: d.client_id }),
    });
    setForm({ request_type: "Additional Coverage" });
    load();
  };

  const send = async () => {
    if (!msg.trim()) return;
    await api("/api/enterprise/messages", {
      method: "POST",
      body: JSON.stringify({ client_id: d.client_id, body: msg }),
    });
    setMsg("");
    load();
  };

  const showInstallBanner =
    !Capacitor.isNativePlatform() && !installed && (installPrompt || isIos());

  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="mx-auto max-w-3xl space-y-5">
        <header>
          <p className="text-sm font-black text-blue-700">JYBEK FAMILY</p>
          <h1 className="text-3xl font-black">Family Mobile Portal</h1>
          <p className="text-slate-600">Schedules, requests, monitored messaging and visit history.</p>
        </header>

        {showInstallBanner && (
          <section className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="font-black text-blue-900">Install this as an app</h2>
                <p className="text-sm text-blue-800">Add Jybek Family Portal to your home screen for one-tap access.</p>
              </div>
              <button
                onClick={handleInstallClick}
                className="shrink-0 rounded-xl bg-blue-700 px-4 py-2 font-bold text-white"
              >
                Install
              </button>
            </div>
            {showIosHelp && (
              <p className="mt-3 text-sm text-blue-800">
                On iPhone/iPad: tap the <b>Share</b> icon in Safari, then <b>Add to Home Screen</b>.
              </p>
            )}
          </section>
        )}

        {err && <div className="rounded-xl bg-red-50 p-3 text-red-700">{err}</div>}

        <section className="rounded-2xl border bg-white p-4">
          <h2 className="text-xl font-black">Request a Change</h2>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <select
              className="rounded-xl border p-3"
              value={form.request_type}
              onChange={(e) => setForm({ ...form, request_type: e.target.value })}
            >
              <option>Additional Coverage</option>
              <option>Schedule Change</option>
              <option>Cancel Visit</option>
              <option>Care Concern</option>
            </select>
            <input
              className="rounded-xl border p-3"
              type="date"
              onChange={(e) => setForm({ ...form, requested_date: e.target.value })}
            />
            <input
              className="rounded-xl border p-3"
              type="time"
              onChange={(e) => setForm({ ...form, requested_start: e.target.value })}
            />
            <input
              className="rounded-xl border p-3"
              type="time"
              onChange={(e) => setForm({ ...form, requested_end: e.target.value })}
            />
            <textarea
              className="rounded-xl border p-3 md:col-span-2"
              placeholder="Details"
              onChange={(e) => setForm({ ...form, details: e.target.value })}
            />
            <button onClick={request} className="rounded-xl bg-slate-900 p-3 font-bold text-white md:col-span-2">
              Submit Request
            </button>
          </div>
        </section>

        <section className="rounded-2xl border bg-white p-4">
          <h2 className="text-xl font-black">Secure Monitored Chat</h2>
          <p className="text-xs text-slate-500">
            Personal contact details, private work requests and solicitation are blocked and reviewed.
          </p>
          <div className="mt-3 max-h-72 space-y-2 overflow-auto">
            {d?.messages?.map((m) => (
              <div key={m.id} className="rounded-xl bg-slate-100 p-3">
                <div className="text-xs font-bold uppercase">{m.sender_role}</div>
                <div>{m.body_visible}</div>
                <div className="text-xs text-slate-400">{new Date(m.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              className="flex-1 rounded-xl border p-3"
              placeholder="Write a message"
            />
            <button onClick={send} className="rounded-xl bg-blue-700 px-5 font-bold text-white">
              Send
            </button>
          </div>
        </section>

        <section className="rounded-2xl border bg-white p-4">
          <h2 className="text-xl font-black">Upcoming & Recent Visits</h2>
          <div className="mt-3 space-y-2">
            {d?.visits?.map((v, i) => (
              <div key={v.id || i} className="rounded-xl border p-3">
                <div className="font-bold">{v.service_type || v.title || "Home Care Visit"}</div>
                <div className="text-sm text-slate-600">
                  {v.visit_date || v.date || v.start_time} &middot; {v.status || "Scheduled"}
                </div>
              </div>
            ))}
          </div>
        </section>

        {photos.length > 0 && (
          <section className="rounded-2xl border bg-white p-4">
            <h2 className="text-xl font-black">Photos</h2>
            {Object.entries(
              photos.reduce((a, p) => ((a[p.album_date] ??= []).push(p), a), {})
            ).map(([date, items]) => (
              <div key={date} className="mt-3">
                <div className="mb-2 text-xs font-bold uppercase text-slate-500">{date}</div>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {items.map((p) => (
                    <a
                      key={p.id}
                      href={`/api/clients/${d.client_id}/photos/${p.id}/file`}
                      target="_blank"
                      rel="noreferrer"
                      className="block aspect-square overflow-hidden rounded-xl border bg-slate-100"
                    >
                      <img
                        src={`/api/clients/${d.client_id}/photos/${p.id}/file`}
                        alt={p.caption || ""}
                        className="h-full w-full object-cover"
                      />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
